import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import TiptapEditor from "../common/TiptapEditor";
import { X, Upload, Image as ImageIcon, Users } from "lucide-react";
import { staffService } from "../../services/staffService";
import { uploadService } from "../../services/uploadService";
import {
  Staff,
  CreateStaffRequest,
  UpdateStaffRequest,
} from "../../types/landing";

interface StaffFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  staff?: Staff | null; // If provided, we're editing
}

const StaffFormModal: React.FC<StaffFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  staff,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [bio, setBio] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateStaffRequest>();

  const isEditing = !!staff;

  useEffect(() => {
    if (isOpen) {
      if (isEditing && staff) {
        // Populate form with existing staff data
        setValue("name", staff.name);
        setValue("position", staff.position);
        setValue("department", staff.department);
        setValue("email", staff.email);
        setValue("phone", staff.phone);
        setValue("qualifications", staff.qualifications || []);
        setValue("experience", staff.experience);
        setValue("status", staff.status);
        setValue("socialLinks.linkedin", staff.socialLinks?.linkedin);
        setValue("socialLinks.twitter", staff.socialLinks?.twitter);
        setValue("socialLinks.facebook", staff.socialLinks?.facebook);
        setValue("imageUrl", staff.imageUrl || "");
        setBio(staff.bio || "");
      } else {
        // Reset form for new staff
        reset();
        setBio("");
        setSelectedImageFile(null);
      }
    }
  }, [isOpen, isEditing, staff, setValue, reset]);

  const handleImageUpload = (file: File) => {
    setSelectedImageFile(file);
    // Object URL is only a preview placeholder; the real URL is set after upload on submit
    setValue("imageUrl", URL.createObjectURL(file), { shouldValidate: true });
    toast.success(
      "Image selected! It will be uploaded when you submit the form."
    );
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit for staff images
        toast.error("Image size should be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      handleImageUpload(file);
    }
  };

  const onSubmit = async (data: CreateStaffRequest) => {
    setLoading(true);
    try {
      // Start with existing image URL (for editing) or form data
      let imageUrl = isEditing && staff ? staff.imageUrl : data.imageUrl;

      // Upload image only if a new file is selected
      if (selectedImageFile) {
        setUploadingImage(true);
        try {
          const uploadResult = await uploadService.uploadFile(
            selectedImageFile,
            "landingpage/staff"
          );
          if (uploadResult.success && uploadResult.data) {
            imageUrl = uploadResult.data.url;
          } else {
            throw new Error("Failed to upload image");
          }
        } finally {
          setUploadingImage(false);
        }
      }

      const staffData = {
        ...data,
        imageUrl,
        bio,
      };

      if (isEditing && staff) {
        await staffService.updateStaff(
          staff._id,
          staffData as UpdateStaffRequest
        );
        toast.success("Staff updated successfully!");
      } else {
        await staffService.createStaff(staffData);
        toast.success("Staff created successfully!");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save staff. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 z-10 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-600" />
              {isEditing ? "Edit Staff" : "Add Staff"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  {...register("name", { required: "Name is required" })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter full name"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position *
                </label>
                <input
                  type="text"
                  {...register("position", {
                    required: "Position is required",
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.position ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="e.g., Teacher, Principal, Administrator"
                />
                {errors.position && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.position.message}
                  </p>
                )}
              </div>
            </div>

            {/* Department and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department *
                </label>
                <select
                  {...register("department", {
                    required: "Department is required",
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.department ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select department</option>
                  <option value="administration">Administration</option>
                  <option value="teaching">Teaching</option>
                  <option value="support">Support Staff</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="other">Other</option>
                </select>
                {errors.department && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.department.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  {...register("status")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  {...register("email")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="staff@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  {...register("phone")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+1234567890"
                />
              </div>
            </div>

            {/* Qualifications */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Qualifications (one per line)
              </label>
              <textarea
                {...register("qualifications")}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Bachelor of Education&#10;Teaching Certificate&#10;First Aid Certified"
                onChange={(e) => {
                  const qualifications = e.target.value
                    .split("\n")
                    .map((q) => q.trim())
                    .filter((q) => q);
                  setValue("qualifications", qualifications);
                }}
              />
            </div>

            {/* Experience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Experience
              </label>
              <input
                type="text"
                {...register("experience")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 5 years teaching experience"
              />
            </div>

            {/* Social Links */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Social Links
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <input
                    type="url"
                    {...register("socialLinks.linkedin")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="LinkedIn URL"
                  />
                </div>
                <div>
                  <input
                    type="url"
                    {...register("socialLinks.twitter")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Twitter URL"
                  />
                </div>
                <div>
                  <input
                    type="url"
                    {...register("socialLinks.facebook")}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Facebook URL"
                  />
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Image *
              </label>
              <input
                type="hidden"
                {...register("imageUrl", {
                  required: "Profile image is required",
                })}
              />
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="staff-image-upload"
                  disabled={uploadingImage}
                />
                <label
                  htmlFor="staff-image-upload"
                  className={`flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-gray-50 ${
                    uploadingImage ? "opacity-50 cursor-not-allowed" : ""
                  } ${errors.imageUrl ? "border-red-500" : "border-gray-300"}`}
                >
                  <Upload className="h-4 w-4" />
                  {uploadingImage ? "Uploading..." : "Choose Image"}
                </label>
                {watch("imageUrl") && (
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <img
                        src={watch("imageUrl")}
                        alt="Staff preview"
                        className="w-16 h-16 object-cover rounded-md border border-gray-300"
                        onError={(e) => {
                          // Hide broken images
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                        <ImageIcon className="h-3 w-3" />
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedImageFile
                        ? "New image selected"
                        : "Current image"}
                    </div>
                  </div>
                )}
              </div>
              {errors.imageUrl && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.imageUrl.message}
                </p>
              )}
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bio
              </label>
              <TiptapEditor
                value={bio}
                onChange={setBio}
                placeholder="Brief biography of the staff member..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading
                  ? "Saving..."
                  : isEditing
                  ? "Update Staff"
                  : "Add Staff"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StaffFormModal;
